// Helper functions for prebuilt watchlists
// Prebuilt watchlists are fetched from the server via the portfolios API
// with owner_type: "NEWSQUAWK". Since the backend treats them as read-only,
// we allow users to locally override their colour via localStorage. Overrides
// are applied at the usePortfoliosCRUD layer so every downstream consumer
// (widgets, matchers) sees the effective colour.
//
// Overrides used to carry a priority too. Lists have none now — which of two
// highlighted lists wins belongs to each widget (highlightOrder.ts) — so a
// stored priority is no longer applied. It is kept, and still migrated from
// the legacy key, for the one-time conversion to per-widget orders
// (useSeedHighlightOrders), which reads it once.

import { Portfolio } from "@/types/Portfolio";

// Helper to check if a watchlist is prebuilt (Newsquawk-owned)
export const isPrebuiltWatchlist = (portfolio: Portfolio): boolean => {
  return portfolio.owner_type === "NEWSQUAWK";
};

// Overrides stored in localStorage, keyed by portfolio id
const OVERRIDES_KEY = "prebuiltWatchlistOverrides";
// Legacy key (priority-only) — migrated on first read and then removed
const LEGACY_PRIORITY_KEY = "prebuiltWatchlistPriorities";

export type PrebuiltOverride = {
  /** Stored by older builds; read only by the one-time order conversion. */
  priority?: number;
  color?: string;
};

export type PrebuiltOverrideMap = Record<number, PrebuiltOverride>;

const readRaw = (): PrebuiltOverrideMap => {
  try {
    const raw = localStorage.getItem(OVERRIDES_KEY);
    return raw ? (JSON.parse(raw) as PrebuiltOverrideMap) : {};
  } catch {
    return {};
  }
};

const writeRaw = (overrides: PrebuiltOverrideMap): void => {
  try {
    localStorage.setItem(OVERRIDES_KEY, JSON.stringify(overrides));
  } catch {
    // ignore quota / access errors
  }
};

// One-time migration from the old priority-only key.
// Runs on first access after deploy, then removes the legacy key.
let migrated = false;
const migrateLegacy = (): void => {
  if (migrated) return;
  migrated = true;
  try {
    const legacy = localStorage.getItem(LEGACY_PRIORITY_KEY);
    if (!legacy) return;
    const legacyMap = JSON.parse(legacy) as Record<number, number>;
    const current = readRaw();
    for (const [idStr, priority] of Object.entries(legacyMap)) {
      const id = Number(idStr);
      if (!Number.isFinite(id)) continue;
      current[id] = { ...current[id], priority };
    }
    writeRaw(current);
    localStorage.removeItem(LEGACY_PRIORITY_KEY);
  } catch {
    // swallow — migration is best-effort
  }
};

// Tiny pub/sub so React hooks can subscribe to override changes.
// We cache a snapshot reference and only invalidate it on mutation, so
// useSyncExternalStore sees a stable identity between changes (required
// to avoid an infinite render loop).
const listeners = new Set<() => void>();
let cachedSnapshot: PrebuiltOverrideMap | null = null;
const subscribe = (cb: () => void): (() => void) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};
const emit = (): void => {
  cachedSnapshot = null;
  listeners.forEach((l) => l());
};

export const getPrebuiltOverrides = (): PrebuiltOverrideMap => {
  migrateLegacy();
  return readRaw();
};

export const setPrebuiltOverride = (
  id: number,
  patch: PrebuiltOverride
): void => {
  migrateLegacy();
  const overrides = readRaw();
  overrides[id] = { ...overrides[id], ...patch };
  writeRaw(overrides);
  emit();
};

// Clear a single field or all overrides for a given portfolio id.
// Passing no field removes the whole entry.
export const clearPrebuiltOverride = (
  id: number,
  field?: keyof PrebuiltOverride
): void => {
  migrateLegacy();
  const overrides = readRaw();
  if (!overrides[id]) return;
  if (field) {
    delete overrides[id][field];
    if (Object.keys(overrides[id]).length === 0) {
      delete overrides[id];
    }
  } else {
    delete overrides[id];
  }
  writeRaw(overrides);
  emit();
};

// Merge effective values into prebuilt portfolios. USER portfolios pass through.
export const applyPrebuiltOverrides = (portfolios: Portfolio[]): Portfolio[] => {
  const overrides = getPrebuiltOverrides();
  if (Object.keys(overrides).length === 0) return portfolios;
  return portfolios.map((p) => {
    if (!isPrebuiltWatchlist(p)) return p;
    const o = overrides[p.id];
    if (!o) return p;
    return { ...p, color: o.color ?? p.color };
  });
};

// React-friendly subscription for useSyncExternalStore.
// Returns a cached snapshot that only changes when emit() is called —
// this keeps reference identity stable and prevents infinite re-renders.
export const subscribeToPrebuiltOverrides = subscribe;
export const getPrebuiltOverridesSnapshot = (): PrebuiltOverrideMap => {
  migrateLegacy();
  if (cachedSnapshot === null) {
    cachedSnapshot = readRaw();
  }
  return cachedSnapshot;
};
